import {MessageDescriptor} from '@lingui/core';
import {t} from '@lingui/macro';

import {isLikePrivateAddress} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {InlineBannerTypesEnum} from '../../../components/inlineBanner/inlineBanner';
import {ServerModel} from '../../../models/api/serverModel';
import {nullInterpolator} from '../../rules/stringTemplateHelpers';
import {StringOrTemplate} from './stringTemplateModel';

/*
 * Types.
 */

export enum ExternalEndpointFormErrorsEnum {
  INVALID_URL = 'invalid_url',
  INVALID_URL_PROTOCOL = 'invalid_url_protocol',
  INVALID_URL_PRIVATE = 'invalid_url_private',
  NO_MATCHING_SERVER = 'no_matching_server',
}

/*
 * Constants.
 */

export const externalEndpointsErrorsLabels: Record<ExternalEndpointFormErrorsEnum, MessageDescriptor> = {
  [ExternalEndpointFormErrorsEnum.INVALID_URL]: t(
    'external-endpoint-error-invalid',
  )`You need to specify a URL`,

  [ExternalEndpointFormErrorsEnum.INVALID_URL_PROTOCOL]: t(
    'external-endpoint-error-protocol',
  )`Must be an HTTPS URL`,

  [ExternalEndpointFormErrorsEnum.INVALID_URL_PRIVATE]: t(
    'external-endpoint-error-private',
  )`Host must be a publicly available domain`,

  [ExternalEndpointFormErrorsEnum.NO_MATCHING_SERVER]: t(
    'external-endpoint-error-server',
  )`You might need to add this origin to your server list`,
};

export function validateExternalEndpoint(
  template: StringOrTemplate,
  servers?: ReadonlyArray<ServerModel>,
): ExternalEndpointFormErrorsEnum | undefined {
  if (!template) {
    return ExternalEndpointFormErrorsEnum.INVALID_URL;
  }

  try {
    const url = new URL(nullInterpolator(template));

    if (url.protocol !== 'https:') {
      return ExternalEndpointFormErrorsEnum.INVALID_URL_PROTOCOL;
    }

    if (isLikePrivateAddress(url.hostname)) {
      return ExternalEndpointFormErrorsEnum.INVALID_URL_PRIVATE;
    }

    if (servers && servers.every((s) => !url.href.startsWith(s.origin))) {
      return ExternalEndpointFormErrorsEnum.NO_MATCHING_SERVER;
    }

    return undefined;
  } catch (err) {
    return ExternalEndpointFormErrorsEnum.INVALID_URL;
  }
}

export function getExternalEndpointErrorType(
  endpointError: ExternalEndpointFormErrorsEnum,
): InlineBannerTypesEnum {
  return endpointError === ExternalEndpointFormErrorsEnum.NO_MATCHING_SERVER
    ? InlineBannerTypesEnum.WARNING
    : InlineBannerTypesEnum.ERROR;
}
